import { theme } from '@Guidelines/Theme/theme';
import requestDocumentPanel from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Documents/DocumentDetails/Helpers/requestDocumentPanel';
import findContainingItems, {
	type ContainingItem,
} from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Documents/DocumentDetails/Sidebars/DocumentPanelSidebar/Helpers/findContainingItems';
import type { DocumentPanelTarget } from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Documents/DocumentDetails/Signals/documentPanelSignal';
import { css } from '@emotion/react';
import * as React from 'react';
import { useSelector } from '@verclam/synapse';
import { Button } from '@verclam/ui-kit';
import ConditionalLoader from '@Guidelines/Component/Loader/ConditionalLoader';

const wrapperStyle = css`
	display: flex;
	flex-direction: column;
	gap: 0.25rem;
`;

const trailStyle = css`
	display: flex;
	flex-wrap: wrap;
	align-items: center;
	gap: 0.25rem 0.375rem;
	font-family: ${theme.fonts.body};
	font-size: ${theme.fontSizes.small};
	color: ${theme.colors.inkMuted};
`;

const currentStyle = css`
	color: ${theme.colors.ink};
`;

type Props = {
	readonly target: DocumentPanelTarget;
};

type TrailLinkProps = {
	readonly item: ContainingItem;
};

const TrailLink: React.FC<TrailLinkProps> = ({ item }) => {
	const handleOpen = React.useCallback(() => {
		requestDocumentPanel({ type: 'list-item', listKey: item.listKey, index: item.index });
	}, [item.listKey, item.index]);

	return (
		<Button
			type="button"
			link
			size="small"
			label={`${item.listTitle} ${item.index + 1}`}
			onClick={handleOpen}
		/>
	);
};

/**
 * The chain above the open object — list element › nested element › field — each enclosing element a
 * link to its own panel. It is how the phone climbs from a field back to its element. Shown only when
 * the object sits inside at least one list element.
 */
const PanelContextTrail: React.FC<Props> = ({ target }) => {
	const document = useSelector((state) => state.documentsStore.document);

	const trail = React.useMemo(() => {
		if (document === null) {
			return null;
		}

		if (target.type === 'field') {
			const containing = findContainingItems(document, { fieldKey: target.fieldKey });
			const label = document.findFieldByKey(target.fieldKey)?.label;

			return containing === null || label === undefined ? null : { containing, current: label };
		}

		if (target.type === 'list-item' || target.type === 'empty-list') {
			const list = document.findListByKey(target.listKey);
			const containing = findContainingItems(document, { listKey: target.listKey });

			if (list === null || containing === null) {
				return null;
			}

			return {
				containing,
				current: target.type === 'list-item' ? `${list.title} ${target.index + 1}` : list.title,
			};
		}

		return null;
	}, [document, target]);

	return (
		<div css={wrapperStyle}>
			<ConditionalLoader data={trail !== null && trail.containing.length > 0 ? trail : null}>
				{(shown) => (
					<nav aria-label="Contexte" css={trailStyle}>
						{shown.containing.map((item) => (
							<React.Fragment key={`${item.listKey}-${item.index}`}>
								<TrailLink item={item} />
								<span aria-hidden="true">›</span>
							</React.Fragment>
						))}
						<span css={currentStyle} aria-current="location">
							{shown.current}
						</span>
					</nav>
				)}
			</ConditionalLoader>
		</div>
	);
};

export default PanelContextTrail;
